import React, {
  MutableRefObject,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react'
import {Sheet, SheetRef} from 'react-modal-sheet'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {useRegisterBlockingSurface} from '../../../context/blockingSurfaces'
import {cssTheme} from '../../../themes'
import {Button} from '../Button'
import {BottomBar} from '../helpers/BottomBar'
import {Box} from '../helpers/Box'
import {Icon} from '../Icon'
import {NavBar, NavBarElement, NavBarProps, NavBarTitle} from '../NavBar'
import {TRANSITION_DURATION} from '../Overlay'
import useBodyScrollLock from '../Overlay/useBodyScrollLock'
import {Handle, OverlayContent, SheetContent, SheetHeader} from './styles'

export const BOTTOM_SHEET_HEADER_HEIGHT = 64
export const BOTTOM_SHEET_WITH_DRAG_HEADER_HEIGHT = 75

type SymbolicSnapPoints = 'top' | 'nearFull' | 'full'
type InitialSnapPoint = 'middle' | 'bottom'
type NumericSnapPoints = number

interface SnapPoints {
  top: SymbolicSnapPoints
  // from 0.4 to 0.7
  middle: NumericSnapPoints
  // pixel value
  bottom?: NumericSnapPoints
}

interface Props {
  /** Whether the BottomSheet is open */
  isOpen: boolean
  /** Append a custom footer to the BottomSheet */
  footerContent?: ReactNode
  /** Use sticky Bottom Bar component */
  bottomBarContent?: ReactNode
  /** The body of the BottomSheet */
  children: ReactNode
  /** The callback for closing the BottomSheet. Optionally pass in the element that was selected to close. */
  onClose?: (e?: React.SyntheticEvent, element?: string) => void
  /** Whether the bottom sheet should match the height of the content or the full height of the viewport */
  hasFixedHeight?: boolean
  /** How long the animation will last in milliseconds */
  transitionDuration?: number
  /** Pass through className to allow styles overrides */
  className?: string
  /** A prop to disable the drag functionality on closing the bottom sheet */
  disableDrag?: boolean
  /** Callback called when opening animation is ended */
  onOpenEnd?: () => void
  /**
   * Props forwarded to the NavBar rendered at the top of the sheet. `bottomContent`
   * is unsupported — a BottomSheet header has no subtitle row.
   */
  navBarProps?: Omit<NavBarProps, 'isCompact' | 'bottomContent'>
  /** Skip sheet animations (sheet instantly snaps to desired location)  */
  prefersReducedMotion?: boolean
  /** Define if header should have drag handler */
  withDragHandler?: boolean
  /** Define if the bottom sheet is modal */
  isModal?: boolean
  /** Define Bottom Sheet snap points */
  snapPoints?: SnapPoints
  /** Define the initial snap point */
  initialSnapPoint?: InitialSnapPoint
  /** Returns undefined to snapPoints and ignore viewport size (use it when you have inputs inside a bottomSheet) */
  ignoreSnapPoints?: boolean
  /** Remove default content padding if set to false */
  noPaddings?: boolean
  /** Render children in a hidden div when closed, so crawlers can index the content */
  prerenderContent?: boolean
  /** Identifies this surface in the blocking-surface registry, so a component asking whether another surface is open can exclude its own */
  surfaceId?: string
}

interface ContentProps {
  children: ReactNode
}

enum State {
  Unmounted = 'Unmounted',
  Hidden = 'Hidden',
  Visible = 'Visible'
}

const withBorderRadius = `
  border-top-left-radius: ${cssTheme.layout.radius.lg} !important;
  border-top-right-radius: ${cssTheme.layout.radius.lg} !important;
  ${NavBarElement} {
    border-top-left-radius: ${cssTheme.layout.radius.lg};
    border-top-right-radius: ${cssTheme.layout.radius.lg};
  }
`

const withoutBorderRadius = `
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  ${NavBarElement} {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`

export const BottomSheetHeader = styled.div`
  transition: border-radius 0.3s;
  position: relative;
  z-index: 1;
  &.--with-handle {
    padding-top: ${cssTheme.layout.spacing.s250};
  }

  &.--with-border-radius {
    ${withBorderRadius}
  }

  &.--without-border-radius {
    ${withoutBorderRadius}
  }
`

export const BottomSheetHandle = styled(Handle)``

const BottomSheetContainer = styled(Sheet.Container)`
  transition: border-radius 0.3s;
  &.--full {
    max-height: calc(100% - env(safe-area-inset-top)) !important;
  }

  &.--with-border-radius {
    ${withBorderRadius}
  }

  &.--without-border-radius {
    ${withoutBorderRadius}
  }
`

export const BottomSheetContentInner = React.forwardRef<
  HTMLDivElement,
  ContentProps
>(({children}, forwardedRef) => {
  const localRef = useRef<HTMLDivElement>()
  const ref = (forwardedRef || localRef) as RefObject<HTMLDivElement> // Ref is optional but required from now on

  useBodyScrollLock({ref})

  return <OverlayContent ref={ref}>{children}</OverlayContent>
})

BottomSheetContentInner.displayName = 'BottomSheetContentInner'

const BottomSheetContent = styled.div<{noPaddings: boolean}>`
  padding: ${({noPaddings}) =>
    !noPaddings
      ? `${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s500}`
      : '0'};
`
const FooterWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`

const SheetBackdrop = styled(Sheet.Backdrop)`
  border: none !important;
  &.--is-modal {
    pointer-events: auto !important;
    background-color: ${cssTheme.colors.background.neutral.overlay} !important;
  }
  &:not(&.--is-modal) {
    pointer-events: none !important;
    background-color: transparent !important;
  }
`

const getCalculatedSnapPoints = (
  snapPoints: SnapPoints,
  initialSnapPoint: InitialSnapPoint
) => {
  let middleSnapPoint = snapPoints?.middle
  if (snapPoints?.middle < 0.4) {
    middleSnapPoint = 0.4
  }

  const calculatedSnapPoints = [0]
  if (snapPoints?.bottom) {
    calculatedSnapPoints.push(snapPoints.bottom)
  }
  if (middleSnapPoint < 1) {
    calculatedSnapPoints.push(middleSnapPoint)
  }
  calculatedSnapPoints.push(1)

  const fullSnapIndex = calculatedSnapPoints.length - 1
  const middleSnapIndex =
    middleSnapPoint < 1 ? fullSnapIndex - 1 : fullSnapIndex
  const bottomSnapIndex = snapPoints?.bottom ? 1 : middleSnapIndex

  return {
    calculatedSnapPoints,
    fullSnapIndex,
    isExpandable: fullSnapIndex > 1,
    startSnapPoint:
      initialSnapPoint === 'middle' ? middleSnapIndex : bottomSnapIndex
  }
}

const useSnapPointSync = ({
  sheetRef,
  snapPoints,
  snapIndex,
  isEnabled
}: {
  sheetRef: MutableRefObject<SheetRef | undefined>
  snapPoints: number[]
  snapIndex: number
  isEnabled: boolean
}) => {
  const snapKey = `${snapPoints.join(',')}|${snapIndex}`
  const previousSnapKey = useRef(snapKey)

  useEffect(() => {
    if (previousSnapKey.current === snapKey) return
    previousSnapKey.current = snapKey

    if (isEnabled) sheetRef.current?.snapTo(snapIndex)
  }, [isEnabled, sheetRef, snapIndex, snapKey])
}

export const BottomSheet = ({
  isOpen,
  footerContent,
  bottomBarContent,
  children,
  onClose,
  onOpenEnd,
  hasFixedHeight = false,
  transitionDuration = TRANSITION_DURATION,
  className,
  navBarProps = {},
  isModal = true,
  disableDrag = false,
  prefersReducedMotion = false,
  withDragHandler = false,
  snapPoints = {top: 'top', middle: 0.7},
  initialSnapPoint = 'middle',
  ignoreSnapPoints = false,
  noPaddings = false,
  prerenderContent = false,
  surfaceId
}: Props) => {
  const {calculatedSnapPoints, startSnapPoint, fullSnapIndex, isExpandable} =
    getCalculatedSnapPoints(snapPoints, initialSnapPoint)
  const ref = React.useRef<SheetRef>()
  const [state, setState] = useState<State>(State.Unmounted)
  const [currentSnapPoint, setCurrentSnapPoint] = useState(startSnapPoint)

  useRegisterBlockingSurface(isOpen, surfaceId)

  useSnapPointSync({
    sheetRef: ref,
    snapPoints: calculatedSnapPoints,
    snapIndex: startSnapPoint,
    isEnabled: state === State.Visible && !ignoreSnapPoints
  })

  const hasBorderRadius = !(
    isExpandable &&
    currentSnapPoint === fullSnapIndex &&
    snapPoints.top === 'full'
  )

  // react-modal-sheet mounts the portal element **on mount** of the component, this causes issues when combined with Overlays that mount their **on open** (this modal may render behind)
  // By mounting/unmounting on open, modals are rendered in a predictable order (last opened on top) because they create portals on the fly and in-order.
  useEffect(() => {
    // First mount the component
    if (isOpen && state === State.Unmounted) setState(State.Hidden)

    // Then open it in the next render
    if (isOpen && state === State.Hidden) setState(State.Visible)

    // Hide immediately (and unmount via onCloseEnd)
    if (!isOpen && state === State.Visible) setState(State.Hidden)
  }, [isOpen, state])

  const handleCloseAnimation = () => {
    if (onClose) {
      onClose()
    }
  }

  const unMount = () => {
    // When resizing the viewport, BottomSheet is calling onCloseEnd
    // with state === State.Visible. That's not correct and causes
    // the bottom sheet to reopen without need.
    if (state === State.Hidden) {
      setState(State.Unmounted)
    }
  }

  const transitionDurationInSeconds = transitionDuration / 1000
  const springConfig = {
    duration: transitionDurationInSeconds,
    ease: 'linear'
  } as const

  const {
    centerContent,
    endContent: navBarEndContent,
    withDivider: navBarWithDivider,
    variant,
    floating,
    centerAligned = true,
    ...navBarRest
  } = navBarProps

  // Title-bar headers show a divider by default, except when floating over content.
  const withDivider = navBarWithDivider ?? !floating

  // The close button is always appended to any consumer-provided end content.
  const headerEndContent = (
    <Box display="flex" justifyContent="center" alignItems="center">
      {navBarEndContent}
      <Button
        dataId="OverlayDrawerCloseButton"
        variant={variant === 'inverse' ? 'ghostInverse' : 'ghost'}
        size="lg"
        iconEnd={
          <Icon
            name="Close"
            colorPath={
              variant === 'inverse'
                ? 'content.neutral.inverse'
                : 'content.neutral.primary'
            }
          />
        }
        onClick={handleCloseAnimation}
      />
    </Box>
  )

  if (state === State.Unmounted) {
    if (!prerenderContent) return null
    return (
      <div aria-hidden="true" style={{display: 'none'}}>
        {children}
      </div>
    )
  }

  return (
    <Sheet
      className={className}
      ref={ref}
      isOpen={state === State.Visible}
      style={{zIndex: 100}}
      detent={!hasFixedHeight ? 'content' : 'default'}
      onClose={handleCloseAnimation}
      onCloseEnd={unMount}
      onSnap={setCurrentSnapPoint}
      tweenConfig={springConfig}
      disableDrag={disableDrag}
      disableScrollLocking
      // react-modal-sheet's keyboard avoidance sets virtualKeyboard.overlaysContent, which stops Chrome lifting inputs above the keyboard
      avoidKeyboard={false}
      onOpenEnd={onOpenEnd}
      prefersReducedMotion={prefersReducedMotion}
      initialSnap={startSnapPoint}
      snapPoints={ignoreSnapPoints ? undefined : calculatedSnapPoints}
    >
      <BottomSheetContainer
        className={cx(
          snapPoints.top === 'full' && '--full',
          hasBorderRadius && '--with-border-radius',
          !hasBorderRadius && '--without-border-radius'
        )}
        style={{paddingBottom: ref.current?.y}}
      >
        <SheetHeader>
          <BottomSheetHeader
            className={cx(
              withDragHandler && '--with-handle',
              hasBorderRadius && '--with-border-radius',
              !hasBorderRadius && '--without-border-radius'
            )}
          >
            {withDragHandler && <BottomSheetHandle />}
            <NavBar
              {...navBarRest}
              isCompact
              variant={variant}
              floating={floating}
              centerAligned={centerAligned}
              withDivider={withDivider}
              centerContent={
                centerContent && <NavBarTitle>{centerContent}</NavBarTitle>
              }
              endContent={headerEndContent}
            />
          </BottomSheetHeader>
        </SheetHeader>
        <SheetContent disableDrag>
          <BottomSheetContentInner>
            <BottomSheetContent noPaddings={noPaddings}>
              {children}
            </BottomSheetContent>
          </BottomSheetContentInner>
        </SheetContent>
        {footerContent && <FooterWrapper>{footerContent}</FooterWrapper>}
        {bottomBarContent && (
          <FooterWrapper>
            <BottomBar hasDivider>{bottomBarContent}</BottomBar>
          </FooterWrapper>
        )}
      </BottomSheetContainer>
      <SheetBackdrop
        onClick={handleCloseAnimation}
        className={cx(isModal && '--is-modal')}
      />
    </Sheet>
  )
}
